import React, { useState } from 'react';
import { useTheme } from '@emotion/react';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { TabContext, TabPanel } from '@mui/lab';
import BreadcrumbHeader from 'layout/BreadcrumbsHeader';
import ThemeBGWrapper from 'ui-components/ThemeBGWrapper';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { containerRef } from 'utils';
import { FetchAnalysisReports } from 'api/bulk-analysis';
import ToolCard from './components/ToolCard';
import ResourcesTab from './components/ResourcesTab';
import bulkAnalysisImage from 'assets/bulk-deck-analysis-preview.png';
import CustomTabs from 'ui-components/CustomTabs';
import { makeStyles } from "@mui/styles"


const useStyles = makeStyles((theme) => ({
  scrollTabs: {
    '& > .Mui-disabled.MuiTabs-scrollButtons': {
      display: 'none',
      transition: 'all 1s ease'
    }
  }
}));

const ToolsLanding = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tab } = useParams();
  const classes = useStyles();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const investorDetails = useSelector((state) => state?.investorDetails?.data);
  const { data: reports } = FetchAnalysisReports(investorDetails?._id);

  const TABS = [
    { label: 'Tools', value: 'tools' },
    // { label: 'Resources', value: 'resources' },
  ];

  const [currentTab, setCurrentTab] = useState(TABS.find((t) => t.value === tab)?.value || 'tools');

  const hasReports = reports && reports.length > 0;

  const tools = [
    {
      id: 'bulk-pitch-deck-analysis',
      title: 'Bulk Pitch Deck Analysis',
      description:
        'Upload up to 50 pitch decks and get AI-powered scoring based on your investment thesis. Instantly identify the best opportunities.',
      // The Bulk Deck Analysis preview, exported from the Figma at 4x (1484x888 for a
      // 371x222 frame) so it stays crisp on high-DPI screens.
      image: bulkAnalysisImage,
      // The Figma only shows the "has reports" state ("View Report"). The empty state is
      // kept: with no reports the card still routes to /tools/bulk-analysis/start.
      button_text: hasReports ? 'View Report' : 'Analyze Decks',
      is_coming_soon: false,
    },
  ];

  // Figma card: 413x489, radius 18, 1px #ECECF1, white, drop shadow 0/3/14.
  // Passed as cardSx because ToolCard is shared with ResourcesTab.
  const bulkAnalysisCardSx = {
    maxWidth: '413px',
    borderRadius: '18px',
    border: '1px solid #ECECF1',
    boxShadow: '0px 3px 14px rgba(16, 24, 40, 0.06)',
  };

  // Figma "image 46": 371x222, radius 6, 1px gradient border (#7852F8 -> #84AEF6).
  // The gradient ring is a 1px padded gradient background with the image inset on top,
  // since a gradient can't be a plain border-color.
  const bulkAnalysisImageSx = {
    aspectRatio: '371 / 222',
    borderRadius: '6px',
    padding: '1px',
    background: 'linear-gradient(180deg, #7852F8 0%, #84AEF6 100%)',
    '& .MuiCardMedia-root': { borderRadius: '5px', objectFit: 'cover' },
  };

  const handleTabChange = (_, newValue) => {
    setCurrentTab(newValue);
    // navigate(`/tools/${newValue}`, { replace: true });
  };

  return (
    <ThemeBGWrapper>
      <PerfectScrollbar containerRef={containerRef} style={{ height: '100%' }}>
        <BreadcrumbHeader
          title="Tools & Resources"
          breadcrumbItems={[{ title: 'Tools & Resources' }]}
        />
        <TabContext value={currentTab}>
          <Grid xs={12} container bgColor={theme.palette.customColors.yellow_bg} height={"100%"}>
            <Box sx={{ width: '100%', typography: 'body1' }}>

              <Box px={isSmallScreen ? "1rem" : "2rem"} bgColor={theme.palette.customColors.yellow_bg}>
                <CustomTabs data={TABS} value={currentTab} isSmallScreen={isSmallScreen} className={classes} handleChange={handleTabChange} key={"Tabs Tabs"} bgColor="transparent" />
              </Box>
              <TabPanel value="tools" sx={{ padding: 0, mt: 3 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(min(22.4375rem, 100%), 1fr))',
                    gap: { xs: '2rem', sm: '3rem', md: '4rem' },
                    padding: isSmallScreen ? '1rem' : '0rem 2rem 2rem 2rem',
                    justifyItems: { xs: 'center', md: 'start' },
                  }}
                >
                  {tools.map((tool) => (
                    <ToolCard
                      key={tool.id}
                      card={tool}
                      cardSx={bulkAnalysisCardSx}
                      imageSx={bulkAnalysisImageSx}
                      onClick={() => {
                        if (hasReports) {
                          localStorage.removeItem('bulk_analysis_filters');
                          navigate('/bulk-analysis');
                        } else {
                          navigate('/bulk-analysis/start');
                        }
                      }}
                    />
                  ))}
                </Box>
              </TabPanel>

              {/* <TabPanel value="resources" sx={{ padding: 0, mt: 3 }}>
                <ResourcesTab />
              </TabPanel> */}
            </Box>
          </Grid>
        </TabContext>
      </PerfectScrollbar>
    </ThemeBGWrapper>
  );
};

export default ToolsLanding;
